import React, { createContext, FC, useEffect, useMemo, useState } from "react";
import { Outlet } from "react-router-dom";
import "leaflet/dist/leaflet.css";

import MainAsideMenuLayout from "@components/layouts/mainAsideMenuLayout/MainAsideMenuLayout";
import MapLayout from "@components/layouts/mapLayout/MapLayout";

import LocalStorageService from "@api/local/LocalStorageService";
import WebSocketService from "@api/socket/WebSocketService";

import FieldService from "@context/map/fields/fieldsStore";
import UnitService from "@context/map/units/unitsStore";
import { AsWsApi } from "@api/socket/AsWsApi";
import { useTypedSelector } from "@hooks/useTypedSelector";
import Logger from "@utils/logger";
import { applyLoading } from "@pages/dashboard/phases";
import useLeafletPixi, { LeafletPixiContext } from "@pages/monitoring/map/hooks/useLeafletPixi";
import { useGeoZoneSliceActions, useUnitSliceActions } from "@hooks/useActions";
import { AuthState } from "@infobis/as-react-auth";

const connectSocket: (auth: AuthState, onReady: (cb: WebSocketService) => void) => WebSocketService = (auth, onReady) => {
    const {
        getCompanyId,
        getDepartment,
        getServerId,
        getToken,
    } = LocalStorageService;

    const l = window.location;

    const url = `${l.protocol === "https:" ? "wss://" : "ws://"}${l.hostname}${
        ["80", "443", "0", ""].includes(l.port) ? `` : `:${l.port}`
    }/data/play`;

    const optionsConnect = () => ({
        url,
        token: auth.accessToken?.accessToken,
        serverId: auth.accessToken?.serverId,
        companyId: auth.authorizedCompany?.id,
        departmentId: auth.authorizedDepartment?.glid
    });
    const initialRequests = ({ data }: { data: any }): any[] => {
        return [
            {
                cid: data.data.id,
                data: {
                    archiveFrom: null,
                    cid: data.data.id,
                    dynamic: [],
                    from: new Date().toISOString(),
                },
                event: "start",
            },
            {
                cid: data.data.id,
                data: {
                    list: [{ entityType: "fieldOp" }],
                },
                event: "removeSwitchableCalc",
            },
            {
                cid: data.data.id,
                data: {
                    list: [],
                },
                event: "setDynamic",
            },
        ];
    };
    const socketModule = new WebSocketService({
        optionsConnect,
        initialRequests,
        onReady
    });
    socketModule.setListenerMessage(
        "UNITS",
        UnitService.fetchDynamicDataUnits
    );
    socketModule.setListenerMessage(
        "FIELDS",
        FieldService.fetchDynamicDataField
    );
    return socketModule
};


interface ApplicationContext {
    socket: WebSocketService
    pixiContext: LeafletPixiContext | undefined
    wsApi: AsWsApi
}

export const ApplicationContext = createContext<ApplicationContext | undefined>(undefined)

const log = Logger.getLogger("map.page")

const MapPage: FC = (props) => {
    const [ctx, setCtx] = useState<ApplicationContext>();
    const auth = useTypedSelector((state) => state.auth)

    const [dateTime, setDateTime] = useState(() => {
        let st, en;
        en = new Date().toISOString();
        return { st, en };
    });

    const [isLoading, setLoading] = useState(false)
    const pixiContext = useLeafletPixi()
    const { fetch: fetchGeoZones } = useGeoZoneSliceActions()
    const { fetch: fetchUnits } = useUnitSliceActions()
    useEffect(() => {
        let connection: WebSocketService | undefined = undefined
        let cancelled = false
        if (pixiContext) {
            setLoading(true)
            Promise.all([fetchUnits(), fetchGeoZones()]).then(
                () => {
                    if(!cancelled) {
                        setLoading(false)
                        connection = connectSocket(auth, (ws) => setCtx({
                            socket: ws,
                            pixiContext,
                            wsApi: new AsWsApi({ ws })
                        }))
                        log.debug("Socket connection", connection)
                    }
                }
            );
        }
        return () => {
            log.debug("Close socket connection...", connection)
            connection?.close()
            cancelled  = true
        }
    }, [dateTime, setDateTime, pixiContext]);

    const { works } = useTypedSelector((state) => state.geoZoneTabSettings)

    useMemo(() => {
        if (ctx) {
            log.debug("Toggle operation contours", works.enabled, ctx)
            ctx?.wsApi.switchWorksTo(works.enabled)
        }
    }, [ctx, works.enabled])
    return (
        <MainAsideMenuLayout>
            <ApplicationContext.Provider value={ctx}>
                <MapLayout>
                    <Outlet
                        context={{ isLoading }}
                    />
                </MapLayout>
            </ApplicationContext.Provider>
        </MainAsideMenuLayout>
    );
};

export default applyLoading(state => [state.operations], actions => [actions.fetchReferenceOperations])(MapPage);
